import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { formatPhone } from "@/lib/format";
import { ToolPageShell, inputCls, Field, StatusPill } from "@/components/AdminTools";
import { Plus, Trash2, Save, X, Edit2, Mail, Phone, FileText, PackagePlus, Workflow } from "lucide-react";

const SOURCES = ["manual", "screener", "referral", "event", "other"];
const STATUSES = ["new", "contacted", "enrolled", "declined"];
const EMPTY = { name: "", email: "", phone: "", source: "manual", status: "new", notes: "" };

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
};

export default function PortalConsumers() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/consumers");
      setItems(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => {
    load();
  }, []);

  const startNew = () => {
    setEditing("new");
    setForm(EMPTY);
  };
  const startEdit = (it) => {
    setEditing(it.id);
    setForm({
      name: it.name || "",
      email: it.email || "",
      phone: it.phone || "",
      source: it.source || "manual",
      status: it.status || "new",
      notes: it.notes || "",
    });
  };
  const cancel = () => {
    setEditing(null);
    setForm(EMPTY);
  };

  const save = async () => {
    if (!form.name || !form.email) {
      toast.error("Name and email are required.");
      return;
    }
    try {
      const payload = { ...form, phone: form.phone || null, notes: form.notes || null };
      if (editing === "new") {
        await api.post("/consumers", payload);
        toast.success("Consumer added.");
      } else {
        await api.patch(`/consumers/${editing}`, payload);
        toast.success("Consumer updated.");
      }
      cancel();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this consumer? This cannot be undone.")) return;
    try {
      await api.delete(`/consumers/${id}`);
      toast.success("Removed.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const filtered = items.filter((c) => {
    if (filter !== "all" && c.status !== filter) return false;
    if (q) {
      const s = q.toLowerCase();
      return (
        (c.name || "").toLowerCase().includes(s) ||
        (c.email || "").toLowerCase().includes(s) ||
        (c.phone || "").toLowerCase().includes(s)
      );
    }
    return true;
  });

  const counts = STATUSES.reduce((acc, s) => {
    acc[s] = items.filter((c) => c.status === s).length;
    return acc;
  }, {});

  return (
    <ToolPageShell
      eyebrow="Clinic · CRM"
      title="Consumers"
      intro="Your patient pipeline for cash-pay programs. Adds from screener submissions auto-merge here."
      actions={
        <button
          data-testid="consumers-new-btn"
          onClick={startNew}
          className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-4 py-2 text-sm font-semibold hover:bg-slate-800"
        >
          <Plus className="w-3.5 h-3.5" /> Add consumer
        </button>
      }
    >
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button
          type="button"
          data-testid="consumers-filter-all"
          onClick={() => setFilter("all")}
          className={`text-xs font-semibold rounded-full px-3 py-1.5 border ${
            filter === "all" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"
          }`}
        >
          All <span className="opacity-60">· {items.length}</span>
        </button>
        {STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            data-testid={`consumers-filter-${s}`}
            onClick={() => setFilter(s)}
            className={`text-xs font-semibold capitalize rounded-full px-3 py-1.5 border ${
              filter === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200"
            }`}
          >
            {s} <span className="opacity-60">· {counts[s] || 0}</span>
          </button>
        ))}
        <div className="ml-auto">
          <input
            data-testid="consumers-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, phone…"
            className="bg-white border border-slate-200 rounded-full px-3.5 py-1.5 text-xs text-slate-900 w-64 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
          />
        </div>
      </div>

      {editing && (
        <div data-testid="consumers-form" className="rounded-2xl border border-slate-200 bg-white p-6 mb-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Name" required>
              <input
                data-testid="consumers-form-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={inputCls}
              />
            </Field>
            <Field label="Email" required>
              <input
                type="email"
                data-testid="consumers-form-email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={inputCls}
              />
            </Field>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Phone">
              <input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: formatPhone(e.target.value) })}
                inputMode="tel"
                placeholder="(555) 123-4567"
                data-testid="consumer-phone-input"
                className={inputCls}
              />
            </Field>
            <Field label="Source">
              <select
                value={form.source}
                onChange={(e) => setForm({ ...form, source: e.target.value })}
                className={inputCls}
              >
                {SOURCES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Status">
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className={inputCls}
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <Field label="Notes">
            <textarea
              rows={3}
              value={form.notes || ""}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className={`${inputCls} resize-y`}
            />
          </Field>
          <div className="flex items-center gap-2 pt-2">
            <button
              data-testid="consumers-form-save"
              onClick={save}
              className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-4 py-2 text-sm font-semibold hover:bg-slate-800"
            >
              <Save className="w-3.5 h-3.5" /> Save
            </button>
            <button
              onClick={cancel}
              className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 rounded-full px-4 py-2 text-sm font-medium hover:bg-slate-50"
            >
              <X className="w-3.5 h-3.5" /> Cancel
            </button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="consumers-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-[0.16em] font-bold text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Contact</th>
              <th className="text-left px-4 py-3">Source</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Added</th>
              <th className="text-left px-4 py-3">Services</th>
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map((c) => (
              <tr key={c.id} data-testid={`consumer-row-${c.id}`}>
                <td className="px-4 py-3">
                  <div className="font-display font-semibold text-slate-900">{c.name}</div>
                  {c.notes && (
                    <div className="text-[11px] text-slate-500 line-clamp-1 inline-flex items-center gap-1">
                      <FileText className="w-3 h-3" /> {c.notes}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-slate-700">
                  <div className="inline-flex items-center gap-1 text-xs">
                    <Mail className="w-3 h-3 text-slate-400" />
                    {c.email}
                  </div>
                  {c.phone && (
                    <div className="inline-flex items-center gap-1 text-xs text-slate-500 mt-0.5">
                      <Phone className="w-3 h-3 text-slate-400" />
                      {c.phone}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 capitalize text-slate-700">{c.source}</td>
                <td className="px-4 py-3">
                  <StatusPill status={c.status} />
                </td>
                <td className="px-4 py-3 text-slate-500 text-xs">{fmt(c.created_at)}</td>
                <td className="px-4 py-3">
                  <Link
                    to={`/portal/consumers/${c.id}`}
                    data-testid={`consumer-services-${c.id}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 rounded-full px-2.5 py-1 transition"
                    title="Order new service or manage existing services & subscriptions"
                  >
                    <PackagePlus className="w-3 h-3" /> Order / Manage
                  </Link>
                  <Link
                    to={`/portal/workflows?consumer=${encodeURIComponent((c.name || "").split(/\s+/)[0] || "")}`}
                    data-testid={`consumer-active-programs-${c.id}`}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-900 bg-emerald-50 hover:bg-emerald-100 rounded-full px-2.5 py-1 transition ml-1.5"
                    title="See this consumer's active programs"
                  >
                    <Workflow className="w-3 h-3" /> Active Programs
                  </Link>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button
                    onClick={() => startEdit(c)}
                    data-testid={`consumer-edit-${c.id}`}
                    className="inline-flex items-center text-xs font-semibold text-slate-700 hover:text-slate-900 mr-3"
                  >
                    <Edit2 className="w-3 h-3 mr-1" /> Edit
                  </button>
                  <button
                    onClick={() => remove(c.id)}
                    data-testid={`consumer-delete-${c.id}`}
                    className="inline-flex items-center text-xs text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center text-slate-500 py-10">
                  {items.length === 0
                    ? "No consumers yet — add one or deploy a screener."
                    : "No matches for current filter."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </ToolPageShell>
  );
}
